import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Text,
  Button,
  Group,
  LoadingOverlay,
  Alert,
  Tooltip,
} from "@mantine/core";
import { IconEdit, IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";
import { StandardTable, StandardTableColumn } from "../common/StandardTable";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import {
  Role,
  WorkflowPermissions,
  GetRolePermissionsResponse,
} from "../../types/workflowPermissions";
import {
  mergeWithDefaults,
  getOverrides,
  ROLE_DESCRIPTIONS,
} from "../../config/defaultPermissions";
import WorkflowPermissionsModal from "./WorkflowPermissionsModal";
import styles from "./PermissionsSettings.module.css";

// Convert ROLE_DESCRIPTIONS record to array for iteration
const ROLES: { role: Role; name: string; description: string }[] = (
  ["Owner", "Manager", "Employee", "Assistant"] as Role[]
).map((role) => ({
  role,
  name: ROLE_DESCRIPTIONS[role].name,
  description: ROLE_DESCRIPTIONS[role].description,
}));

// Type for SalonContext values used in this component
interface SalonContextValue {
  businessInfo: { id: string } | null;
  currentUser: { role: string } | null;
}

const PermissionsSettings: React.FC = () => {
  const { businessInfo, currentUser } = useSalon() as SalonContextValue;
  const { getRolePermissions } = useApp();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionsData, setPermissionsData] =
    useState<GetRolePermissionsResponse | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [modalOpened, setModalOpened] = useState(false);

  const fetchPermissions = useCallback(
    async (showLoading = true) => {
      if (!businessInfo?.id) return;

      try {
        if (showLoading) {
          setIsLoading(true);
        }
        setError(null);
        const data = await getRolePermissions(businessInfo.id);
        setPermissionsData(data);
      } catch (err) {
        console.error("Error fetching permissions:", err);
        setError("Failed to load permissions. Please try again.");
      } finally {
        if (showLoading) {
          setIsLoading(false);
        }
      }
    },
    [businessInfo?.id, getRolePermissions],
  );

  useEffect(() => {
    fetchPermissions(true);
  }, [fetchPermissions]);

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setModalOpened(true);
  };

  const handleModalClose = () => {
    setModalOpened(false);
    setSelectedRole(null);
  };

  const handlePermissionUpdate = useCallback(
    (updatedPermissions?: WorkflowPermissions) => {
      if (updatedPermissions && selectedRole && permissionsData) {
        setPermissionsData({
          ...permissionsData,
          permissions: {
            ...permissionsData.permissions,
            [selectedRole]: updatedPermissions,
          },
          overrides: {
            ...permissionsData.overrides,
            [selectedRole]: getOverrides(selectedRole, updatedPermissions),
          },
        });
      } else {
        fetchPermissions(false);
      }
    },
    [fetchPermissions, selectedRole, permissionsData],
  );

  // Role permissions management is only available to Owners and Managers
  const hasAccess =
    currentUser?.role === "Owner" || currentUser?.role === "Manager";

  if (!hasAccess) {
    return (
      <Stack p="md">
        <Title order={3} fw={500}>
          Role Permissions
        </Title>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Access Restricted"
          color="yellow"
        >
          Permission settings are only available to Owners and Managers.
        </Alert>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack p="md" pos="relative" mih={200}>
        <LoadingOverlay visible={isLoading} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack p="md">
        <Title order={3} fw={500}>
          Role Permissions
        </Title>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
        <Button onClick={() => fetchPermissions(true)} color="teal">
          Retry
        </Button>
      </Stack>
    );
  }

  return (
    <Stack p="md" className={styles.container}>
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={3} fw={500}>
            Role Permissions
          </Title>
          <Text size="sm" c="dimmed">
            Manage what each role can see or do within your studio
          </Text>
        </Stack>
      </Group>

      <StandardTable<(typeof ROLES)[number]>
        columns={
          [
            {
              key: "name",
              label: "Role",
              render: (roleDesc) => {
                const isOwner = roleDesc.role === "Owner";
                return (
                  <Group gap={4} align="flex-start">
                    <Text fw={500}>{roleDesc.name}</Text>
                    {isOwner && (
                      <Tooltip
                        label="Owner role has full access to all features and cannot be restricted"
                        multiline
                        w={250}
                        withArrow
                      >
                        <IconInfoCircle
                          size={12}
                          style={{
                            color: "var(--mantine-color-dark-4)",
                            cursor: "help",
                            marginTop: 2,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Group>
                );
              },
            },
            {
              key: "description",
              label: "Description",
              render: (roleDesc) => (
                <Text size="sm" c="dimmed">
                  {roleDesc.description}
                </Text>
              ),
            },
            {
              key: "actions",
              label: "Actions",
              width: 110,
              align: "right",
              render: (roleDesc) =>
                roleDesc.role === "Owner" ? null : (
                  <Button
                    variant="subtle"
                    color="teal"
                    size="xs"
                    leftSection={<IconEdit size={14} />}
                    onClick={() => handleEditRole(roleDesc.role)}
                  >
                    Edit
                  </Button>
                ),
            },
          ] as StandardTableColumn<(typeof ROLES)[number]>[]
        }
        data={ROLES}
        getRowKey={(r) => r.role}
        ariaLabel="Role permissions"
      />

      {selectedRole && permissionsData && businessInfo && (
        <WorkflowPermissionsModal
          opened={modalOpened}
          onClose={handleModalClose}
          role={selectedRole}
          salonId={businessInfo.id}
          initialPermissions={mergeWithDefaults(
            selectedRole,
            permissionsData.permissions?.[selectedRole] ?? {},
          )}
          onPermissionUpdate={handlePermissionUpdate}
        />
      )}
    </Stack>
  );
};

export default PermissionsSettings;
